import { useCallback, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { corporationFormSchema } from "../../../../../validations/schemas";
import { useTranslation } from "react-i18next";
import {
    Avatar,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Typography,
} from "@mui/material";
import { MuiChipsInput } from "mui-chips-input";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import { useStyles } from "./CorporationForm.styles";
import {
    ICorporateFormValues,
    ICorporateReponse,
    ICorporationForm,
    IDivision,
    IRegion,
} from "./CorporationFormInterfaces";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { CorporationsEntity } from "../../../../../entities/Corporations/CorporationsEntity";
import { IInsertCorporationWithAdmin } from "../../../../../entities/Corporations/CorporationsEntityInterfaces";
import { USA_COUNTRY_ID, USERTYPE } from "../../../../../constants/constants";
import { uploadCorporateImage } from "api/queries/corporationsAPI";
import UploadDropZone from "components/UploadDropZone/UploadDropZone.view";
import { emailRegEx } from "validations/matchers";
import GooglePlacesAddressFields from "components/GooglePlacesAddressFields";

export const CorporationForm = ({ closeForm, nextStep, corporationEditData }: ICorporationForm) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { apolloMutationError, upsertCorporationWithAdmin } =
        CorporationsEntity.UpsertCorporationWithAdminMaderaBackoffice();
    const [editDivisionField, setEditDivisionField] = useState<number | null>(null);
    const [showEmailExists, setShowEmailExists] = useState<boolean>(false);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);

    const initFormValues: ICorporateFormValues = useMemo(
        () => ({
            name: corporationEditData?.name || "",
            address1: corporationEditData?.address_1 || "",
            address2: corporationEditData?.address_2 || "",
            country_id: corporationEditData?.country_id || USA_COUNTRY_ID,
            phoneNumber: corporationEditData?.phone_number || "",
            city_name: corporationEditData?.city_name || "",
            state_name: corporationEditData?.state_name || "",
            firstName: "",
            lastName: "",
            email: "",
            users: corporationEditData?.users || [],
            zipCode: corporationEditData?.zip_code || "",
            divisionInput: "",
            divisionEditInput: "",
            divisions: corporationEditData?.divisions || [],
            dont_add_user: Boolean(corporationEditData),
        }),
        [corporationEditData],
    );

    const handleSubmit = useCallback(
        async (values: ICorporateFormValues) => {
            const corporateValues: IInsertCorporationWithAdmin = {
                ...(corporationEditData && { corporate_id: corporationEditData.corporate_id }),
                address_1: values.address1,
                address_2: values.address2,
                country_id: values.country_id,
                name: values.name,
                phone_number: values.phoneNumber,
                city_name: values.city_name || null,
                state_name: values.state_name || null,
                zip_code: values.zipCode,
                ...(!corporationEditData && {
                    divisions: {
                        data: values.divisions.map((division) => ({
                            ...division,
                            regions: { data: division.regions },
                        })),
                    },
                }),
                ...(!corporationEditData
                    ? {
                          users: {
                              data: [
                                  {
                                      first_name: values.firstName,
                                      last_name: values.lastName,
                                      email: values.email,
                                      user_type_id: USERTYPE.ADMINISTRATOR,
                                  },
                              ],
                          },
                      }
                    : {
                          users: {
                              data: values.users.filter((user) => !user.user_id && user.user_type_id),
                          },
                      }),
            };

            let deepClonedDivisions: IDivision[] = JSON.parse(JSON.stringify(values.divisions));
            const divisionValues = corporationEditData
                ? deepClonedDivisions.map((division) => {
                      delete division.__typename;
                      return {
                          ...division,
                          corporate_id: corporationEditData.corporate_id,
                          regions: {
                              data: division.regions.map((region) => {
                                  delete region.__typename;
                                  delete region.mills;
                                  return { ...region };
                              }),
                              on_conflict: {
                                  constraint: "region_pkey",
                                  update_columns: ["name", "deleted_at"],
                              },
                          },
                      };
                  })
                : [];

            try {
                const response = await upsertCorporationWithAdmin({
                    variables: { corporate: corporateValues, division: divisionValues },
                });
                if (response) {
                    const corporation: ICorporateReponse = response.data.insert_corporate.returning[0];

                    if (filesToUpload && filesToUpload.length > 0) {
                        const formData = new FormData();
                        formData.set("file", filesToUpload[0] as File);
                        formData.set("corporate_id", corporation.corporate_id.toString());
                        await uploadCorporateImage(corporation.corporate_id, formData);
                    }

                    nextStep(corporation);
                }
            } catch {
                return;
            }
        },
        [upsertCorporationWithAdmin, filesToUpload, nextStep, corporationEditData],
    );
    return (
        <div>
            <div className={classes.titleContainer}>
                <IconButton onClick={() => closeForm()}>
                    <ArrowBackIosIcon />
                </IconButton>
                <h1 className={classes.formTitle}>
                    {corporationEditData ? t("setup_corporation.form_edit_title") : t("setup_corporation.form_title")}
                </h1>
            </div>
            <Formik initialValues={initFormValues} validationSchema={corporationFormSchema} onSubmit={handleSubmit}>
                {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                    return (
                        <Form className={classes.fieldsContainer}>
                            <TextField
                                fullWidth
                                id="outlined-name-input"
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                className={classes.textField}
                                label={t("setup_corporation.corporation_form.name")}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                            <TextField
                                fullWidth
                                id="outlined-phone-number-input"
                                name="phoneNumber"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.corporation_form.phone_number")}
                                value={values.phoneNumber}
                                onChange={handleChange}
                                error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                helperText={touched.phoneNumber && errors.phoneNumber && t(errors.phoneNumber)}
                            />

                            <GooglePlacesAddressFields initialValues={initFormValues} />

                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>
                                    {corporationEditData
                                        ? t("setup_corporation.divisions_edit_title")
                                        : t("setup_corporation.divisions_title")}
                                </h3>
                            </div>
                            <TextField
                                fullWidth
                                id="outlined-division-input"
                                name="divisionInput"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.corporation_form.division")}
                                value={values.divisionInput}
                                error={
                                    touched.divisions &&
                                    typeof errors.divisions === "string" &&
                                    Boolean(errors.divisions)
                                }
                                helperText={
                                    touched.divisions &&
                                    errors.divisions &&
                                    typeof errors.divisions === "string" &&
                                    t(errors.divisions.toString())
                                }
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        if (Boolean(values.divisionInput)) {
                                            const duplicatedValues = values.divisions.findIndex((division) => {
                                                return (
                                                    division.name.toLowerCase() ===
                                                    values.divisionInput.trim().toLowerCase()
                                                );
                                            });

                                            if (duplicatedValues !== -1) {
                                                setFieldValue("divisionInput", "");
                                                return;
                                            }

                                            setFieldValue("divisions", [
                                                ...values.divisions,
                                                {
                                                    name: values.divisionInput,
                                                    regions: [],
                                                },
                                            ]);

                                            setFieldValue("divisionInput", "");
                                        }
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onClick={() => {
                                                if (Boolean(values.divisionInput)) {
                                                    const duplicatedValues = values.divisions.findIndex((division) => {
                                                        return (
                                                            division.name.toLowerCase() ===
                                                            values.divisionInput.trim().toLowerCase()
                                                        );
                                                    });

                                                    if (duplicatedValues !== -1) {
                                                        setFieldValue("divisionInput", "");
                                                        return;
                                                    }

                                                    setFieldValue("divisions", [
                                                        ...values.divisions,
                                                        {
                                                            name: values.divisionInput,
                                                            regions: [],
                                                        },
                                                    ]);

                                                    setFieldValue("divisionInput", "");
                                                }
                                            }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    ),
                                }}
                                onChange={handleChange}
                            ></TextField>
                            {Boolean(values.divisions.length) && (
                                <List className={classes.listContainer}>
                                    {values.divisions
                                        .filter((division) => !division.deleted_at)
                                        .map((division, index) => {
                                            const regions: {
                                                mills?: { mill_id: number }[];
                                                region_id?: number | undefined;
                                                name: string;
                                                deleted_at?: Date;
                                            }[] = division.regions;
                                            const divisionWithMill =
                                                regions.filter((region) =>
                                                    region.mills ? region.mills.length > 0 : false,
                                                ).length > 0;

                                            const divisionIndex = values.divisions.findIndex(
                                                (div) => division.name === div.name,
                                            );

                                            const handleChangeRegions = (newRegions: string[]) => {
                                                let deepClonedDivisions: IDivision[] = JSON.parse(
                                                    JSON.stringify(values.divisions),
                                                );

                                                const toFindDuplicates = (array: string[]) =>
                                                    array.filter(
                                                        (item, index) =>
                                                            array
                                                                .map((element) => element.toLowerCase())
                                                                .indexOf(item.toLowerCase()) !== index,
                                                    );
                                                const duplicateElements = toFindDuplicates(newRegions);
                                                if (duplicateElements.length) return;

                                                deepClonedDivisions[divisionIndex] = {
                                                    ...deepClonedDivisions[divisionIndex],
                                                    regions: newRegions.map((newRegion, index) => {
                                                        const existingIndex =
                                                            deepClonedDivisions[divisionIndex].regions[index];
                                                        return existingIndex
                                                            ? {
                                                                  ...deepClonedDivisions[divisionIndex].regions[index],
                                                                  name: newRegion,
                                                              }
                                                            : { name: newRegion };
                                                    }),
                                                };
                                                setFieldValue("divisions", deepClonedDivisions);
                                            };

                                            return (
                                                <div key={division.name} className={classes.separator}>
                                                    <ListItem key={index} className={classes.listItem}>
                                                        {editDivisionField !== index && (
                                                            <ListItemText
                                                                className={classes.listText}
                                                                primary={division.name}
                                                            />
                                                        )}
                                                        {editDivisionField === index && (
                                                            <TextField
                                                                fullWidth
                                                                label={t(
                                                                    "setup_corporation.corporation_form.edit_name_division",
                                                                )}
                                                                variant="outlined"
                                                                name="divisionEditInput"
                                                                onChange={handleChange}
                                                                value={values.divisionEditInput}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === "Enter") {
                                                                        event.preventDefault();

                                                                        if (Boolean(values.divisionEditInput)) {
                                                                            const divisions = [...values.divisions];
                                                                            divisions[divisionIndex] = {
                                                                                ...divisions[divisionIndex],
                                                                                name: values.divisionEditInput,
                                                                            };

                                                                            setFieldValue("divisions", divisions);
                                                                            setEditDivisionField(null);
                                                                        }
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <>
                                                                            <IconButton
                                                                                onClick={() => {
                                                                                    if (
                                                                                        Boolean(
                                                                                            values.divisionEditInput,
                                                                                        )
                                                                                    ) {
                                                                                        const divisions = [
                                                                                            ...values.divisions,
                                                                                        ];
                                                                                        divisions[divisionIndex] = {
                                                                                            ...divisions[divisionIndex],
                                                                                            name: values.divisionEditInput,
                                                                                        };

                                                                                        setFieldValue(
                                                                                            "divisions",
                                                                                            divisions,
                                                                                        );
                                                                                        setEditDivisionField(null);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <CheckIcon />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    setEditDivisionField(null)
                                                                                }
                                                                            >
                                                                                <CloseIcon />
                                                                            </IconButton>
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                        {editDivisionField !== index && (
                                                            <>
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setFieldValue(
                                                                            "divisionEditInput",
                                                                            division.name,
                                                                        );
                                                                        setEditDivisionField(index);
                                                                    }}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                                {divisionWithMill ? (
                                                                    <Tooltip
                                                                        PopperProps={{
                                                                            className: classes.tooltip,
                                                                        }}
                                                                        placement="top"
                                                                        title={
                                                                            <Typography
                                                                                className={classes.tooltipTexts}
                                                                            >
                                                                                {t(
                                                                                    "setup_corporation.corporation_form.no_deletion_division_note",
                                                                                )}
                                                                            </Typography>
                                                                        }
                                                                    >
                                                                        <div>
                                                                            <IconButton disabled={true}>
                                                                                <DeleteIcon />
                                                                            </IconButton>
                                                                        </div>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <IconButton
                                                                        onClick={() => {
                                                                            if (regions.length > 0) {
                                                                                setFieldValue(
                                                                                    "divisions",
                                                                                    values.divisions
                                                                                        .map((divisionDelete) => {
                                                                                            if (
                                                                                                divisionDelete.name ===
                                                                                                    division.name &&
                                                                                                divisionDelete.division_id
                                                                                            ) {
                                                                                                return {
                                                                                                    ...divisionDelete,
                                                                                                    deleted_at:
                                                                                                        new Date(),
                                                                                                    regions:
                                                                                                        divisionDelete.regions
                                                                                                            .map(
                                                                                                                (
                                                                                                                    region,
                                                                                                                ) => {
                                                                                                                    if (
                                                                                                                        region.region_id
                                                                                                                    ) {
                                                                                                                        return {
                                                                                                                            ...region,
                                                                                                                            deleted_at:
                                                                                                                                new Date(),
                                                                                                                        };
                                                                                                                    } else
                                                                                                                        return region;
                                                                                                                },
                                                                                                            )
                                                                                                            .filter(
                                                                                                                (
                                                                                                                    region,
                                                                                                                ) =>
                                                                                                                    region?.region_id,
                                                                                                            ),
                                                                                                };
                                                                                            } else
                                                                                                return divisionDelete;
                                                                                        })
                                                                                        .filter((divisionDelete) =>
                                                                                            divisionDelete.division_id
                                                                                                ? true
                                                                                                : divisionDelete.name !==
                                                                                                  division.name,
                                                                                        ),
                                                                                );
                                                                            } else {
                                                                                setFieldValue(
                                                                                    "divisions",
                                                                                    values.divisions
                                                                                        .map((divisionDelete) => {
                                                                                            if (
                                                                                                divisionDelete.name ===
                                                                                                    division.name &&
                                                                                                divisionDelete.division_id
                                                                                            ) {
                                                                                                return {
                                                                                                    ...divisionDelete,
                                                                                                    deleted_at:
                                                                                                        new Date(),
                                                                                                };
                                                                                            } else
                                                                                                return divisionDelete;
                                                                                        })
                                                                                        .filter((divisionDelete) =>
                                                                                            divisionDelete.division_id
                                                                                                ? true
                                                                                                : divisionDelete.name !==
                                                                                                  division.name,
                                                                                        ),
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                )}
                                                            </>
                                                        )}
                                                    </ListItem>
                                                    <MuiChipsInput
                                                        value={regions.map((region) => region.name)}
                                                        onChange={handleChangeRegions}
                                                        className={classes.chips}
                                                        error={Boolean(
                                                            touched.divisions &&
                                                                errors.divisions?.length &&
                                                                errors.divisions[index],
                                                        )}
                                                        helperText={
                                                            touched.divisions &&
                                                            errors.divisions?.length &&
                                                            errors.divisions[index] &&
                                                            typeof errors.divisions == "object" &&
                                                            t("field_errors.at_least_one_item_required")
                                                        }
                                                        label={t("setup_corporation.corporation_form.region")}
                                                        renderChip={(Component, key, props) => {
                                                            const regionIndex = regions.findIndex(
                                                                (region) => region.name === props.label,
                                                            );
                                                            const region = regions[regionIndex]?.mills;
                                                            const hasMill: boolean = region ? region.length > 0 : false;
                                                            const deleted = regions[regionIndex]?.deleted_at;

                                                            return (
                                                                <Component
                                                                    style={{ display: deleted ? "none" : "" }}
                                                                    {...props}
                                                                    key={key}
                                                                    deleteIcon={
                                                                        hasMill ? (
                                                                            <Tooltip
                                                                                PopperProps={{
                                                                                    className: classes.tooltip,
                                                                                }}
                                                                                placement="top"
                                                                                title={
                                                                                    <Typography
                                                                                        className={classes.tooltipTexts}
                                                                                    >
                                                                                        {t(
                                                                                            "setup_corporation.corporation_form.no_deletion_region_note",
                                                                                        )}
                                                                                    </Typography>
                                                                                }
                                                                            >
                                                                                <CancelIcon />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <CancelIcon />
                                                                        )
                                                                    }
                                                                    onDelete={() => {
                                                                        let deepClonedDivisions: IDivision[] =
                                                                            JSON.parse(
                                                                                JSON.stringify(values.divisions),
                                                                            );
                                                                        let deepClonedRegions: IRegion[] = JSON.parse(
                                                                            JSON.stringify(regions),
                                                                        );

                                                                        if (
                                                                            deepClonedRegions[regionIndex]?.mills
                                                                                ?.length
                                                                        )
                                                                            return;

                                                                        if (deepClonedRegions[regionIndex]?.region_id) {
                                                                            deepClonedRegions[regionIndex] = {
                                                                                ...deepClonedRegions[regionIndex],
                                                                                deleted_at: new Date(),
                                                                            };
                                                                            deepClonedDivisions[divisionIndex] = {
                                                                                ...deepClonedDivisions[divisionIndex],
                                                                                regions: deepClonedRegions,
                                                                            };
                                                                            setFieldValue(
                                                                                "divisions",
                                                                                deepClonedDivisions,
                                                                            );
                                                                        } else {
                                                                            deepClonedDivisions[divisionIndex] = {
                                                                                ...deepClonedDivisions[divisionIndex],
                                                                                regions: deepClonedRegions.filter(
                                                                                    (region) =>
                                                                                        region.name !==
                                                                                        deepClonedRegions[regionIndex]
                                                                                            .name,
                                                                                ),
                                                                            };
                                                                            setFieldValue(
                                                                                "divisions",
                                                                                deepClonedDivisions,
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                        disableDeleteOnBackspace
                                                        hideClearAll
                                                        placeholder={
                                                            !division.regions.length
                                                                ? t(
                                                                      "setup_corporation.corporation_form.region_placeholder",
                                                                  )
                                                                : ""
                                                        }
                                                    />
                                                </div>
                                            );
                                        })}
                                </List>
                            )}

                            {corporationEditData && (
                                <>
                                    <div className={classes.titleContainer}>
                                        <h3 className={classes.formSubtitle}>
                                            {t("setup_corporation.corporation_form.user_admins")}
                                        </h3>
                                    </div>
                                    {!values.users.length && (
                                        <div className={classes.noAdminsWrapper}>
                                            <InfoIcon color="warning" />
                                            {t("setup_corporation.corporation_form.no_admin_users")}
                                        </div>
                                    )}

                                    {!!values.users.length && (
                                        <List className={classes.listContainer}>
                                            {values.users.map((user, index) => {
                                                return (
                                                    <div key={index} className={classes.separatorNoPadding}>
                                                        <ListItem key={index} className={classes.listItem}>
                                                            <ListItemText
                                                                className={classes.listText}
                                                                primary={
                                                                    <div className={classes.listItemContainer}>
                                                                        {!user.user_id && (
                                                                            <Tooltip
                                                                                PopperProps={{
                                                                                    className: classes.tooltip,
                                                                                }}
                                                                                title={
                                                                                    <Typography
                                                                                        className={classes.tooltipTexts}
                                                                                    >
                                                                                        {t(
                                                                                            "setup_corporation.corporation_form.user_add_saved_permamently",
                                                                                        )}
                                                                                    </Typography>
                                                                                }
                                                                            >
                                                                                <InfoIcon color="warning" />
                                                                            </Tooltip>
                                                                        )}
                                                                        <p
                                                                            className={classes.listContainerTitle}
                                                                        >{`${user.first_name} ${user.last_name}`}</p>
                                                                        <p className={classes.listContainerEmail}>
                                                                            ({user.email})
                                                                        </p>
                                                                    </div>
                                                                }
                                                            />
                                                            {!user.user_id && (
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setFieldValue(
                                                                            "users",
                                                                            values.users.filter(
                                                                                (editUser) =>
                                                                                    editUser.email !== user.email,
                                                                            ),
                                                                        );
                                                                    }}
                                                                    disabled={!!user.user_id}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            )}
                                                        </ListItem>
                                                    </div>
                                                );
                                            })}
                                        </List>
                                    )}
                                </>
                            )}

                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>
                                    {corporationEditData
                                        ? t("setup_corporation.add_administrator")
                                        : t("setup_corporation.assign_administrator")}
                                </h3>
                            </div>
                            <TextField
                                fullWidth
                                id="outlined-firstName-input"
                                name="firstName"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.corporation_form.first_name")}
                                value={values.firstName}
                                onChange={handleChange}
                                error={touched.firstName && Boolean(errors.firstName)}
                                helperText={touched.firstName && errors.firstName && t(errors.firstName)}
                            />
                            <TextField
                                fullWidth
                                id="outlined-lastName-input"
                                name="lastName"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.corporation_form.last_name")}
                                value={values.lastName}
                                onChange={handleChange}
                                error={touched.lastName && Boolean(errors.lastName)}
                                helperText={touched.lastName && errors.lastName && t(errors.lastName)}
                            />
                            <TextField
                                fullWidth
                                id="outlined-email-input"
                                name="email"
                                type="text"
                                variant="outlined"
                                className={classes.textField}
                                label={t("setup_corporation.corporation_form.email")}
                                value={values.email}
                                onChange={handleChange}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email && t(errors.email)}
                            />

                            <p
                                style={{
                                    display: apolloMutationError || showEmailExists ? "block" : "none",
                                    color: "red",
                                }}
                            >
                                {apolloMutationError?.message.includes("uq_corporate_name")
                                    ? t("setup_corporation.errors.existing_name")
                                    : apolloMutationError?.message.includes("uq_user_email") || showEmailExists
                                    ? t("setup_corporation.errors.existing_email")
                                    : t("setup_corporation.errors.creation_error")}
                            </p>

                            {corporationEditData && (
                                <div className={classes.addAdminActions}>
                                    <Button
                                        className={classes.submitButton}
                                        variant="contained"
                                        disabled={
                                            isSubmitting ||
                                            !values.firstName ||
                                            !values.lastName ||
                                            !values.email ||
                                            emailRegEx.test(values.email) === false
                                        }
                                        onClick={() => {
                                            const emailExists = values.users.find(
                                                (user) => user.email === values.email,
                                            );

                                            if (emailExists) {
                                                setShowEmailExists(true);
                                            } else {
                                                setShowEmailExists(false);
                                            }

                                            if (values.firstName && values.lastName && values.email && !emailExists) {
                                                setFieldValue("users", [
                                                    ...values.users,
                                                    {
                                                        first_name: values.firstName,
                                                        last_name: values.lastName,
                                                        email: values.email,
                                                        user_type_id: USERTYPE.ADMINISTRATOR,
                                                    },
                                                ]);
                                                setFieldValue("firstName", "");
                                                setFieldValue("lastName", "");
                                                setFieldValue("email", "");
                                            }
                                        }}
                                    >
                                        {t("setup_corporation.corporation_form.add_admin_button")}
                                    </Button>
                                </div>
                            )}

                            <div className={classes.titleContainer}>
                                <h3 className={classes.formSubtitle}>{t("setup_corporation.corporation_form.logo")}</h3>
                            </div>
                            {corporationEditData?.media?.media_url && !filesToUpload.length && (
                                <Avatar className={classes.avatar} src={corporationEditData.media.media_url} />
                            )}
                            <UploadDropZone
                                onFilesChange={(files) => setFilesToUpload(files)}
                                hintText={t("setup_corporation.corporation_form.size_recomendation")}
                            />
                            <div className={classes.titleContainer}>
                                {!!corporationEditData && (
                                    <Button
                                        className={classes.cancelButton}
                                        variant="contained"
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={closeForm}
                                    >
                                        {t("setup_corporation.mill_form.cancel_button")}
                                    </Button>
                                )}
                                <Button
                                    className={classes.submitButton}
                                    variant="contained"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {corporationEditData
                                        ? t("setup_corporation.form_submit_edit")
                                        : t("setup_corporation.form_submit")}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
